
import { IBarcode, ITrackingData } from '@/utils/types/barcodeTypes'
import { ISensor } from '@/utils/types/fieldDefinition'
import { IProject } from '@/utils/types/projectTypes'
import * as am4charts from '@amcharts/amcharts4/charts'
import * as am4core from '@amcharts/amcharts4/core'
import m4themesAnimated from '@amcharts/amcharts4/themes/animated'
import { Options, Vue } from 'vue-class-component'
import { Prop, Ref, Watch } from 'vue-property-decorator'
import KeyenceHTHardKeyButton from '@/components/keyenceHT/hardkeys/KeyenceHTHardKeyButton.vue'
import { KeyenceHardKey } from '@/utils/keyenceHT/keyenceHardKeyEventHandler'
import { isKeyenceEnterpriseBrowser } from '@/utils/keyenceHT/keyenceModules'
am4core.useTheme(m4themesAnimated)
am4core.options.autoDispose = true

@Options({
  components: {
    KeyenceHTHardKeyButton,
  },
  emits: ['dialog:close'],
})
export default class SensorDialog extends Vue {
  KeyenceHardKey = KeyenceHardKey

  get isKeyenceEnterpriseBrowser() {
    return isKeyenceEnterpriseBrowser()
  }

  emitDialogClose() {
    this.$emit('dialog:close')
  }

  @Prop({ type: Object }) readonly project!: IProject
  @Prop({ type: Object }) readonly barcode!: IBarcode
  @Prop({ type: Boolean }) readonly isShowDialog!: boolean
  @Prop({ type: Number }) readonly trackingDataIndex!: number
  @Ref() temperatureChartContainer!: HTMLElement
  @Ref() humidityChartContainer!: HTMLElement

  @Watch('isShowDialog')
  sensorChanged() {
    if (!this.isShowDialog) {
      this.temperatureChartContainer.innerHTML = ''
      this.humidityChartContainer.innerHTML = ''
      return
    }
    this.$nextTick(() => {
      this.createTemperatureChart()
      this.createHumidityChart()
    })
  }

  createTemperatureChart() {
    const chart = am4core.create(this.temperatureChartContainer, am4charts.XYChart)
    chart.data = this.temperatureFormatData

    // Create axes
    const dateAxis = chart.xAxes.push(new am4charts.DateAxis())
    dateAxis.renderer.minGridDistance = 60

    chart.yAxes.push(new am4charts.ValueAxis())

    // Create series
    const series = chart.series.push(new am4charts.LineSeries())
    series.dataFields.valueY = 'value'
    series.dataFields.dateX = 'date'
    series.tooltipText = '{value}'
    series.strokeWidth = 1
    // series.tensionX = 0.8
    series.bullets.push(new am4charts.CircleBullet())

    // series.tooltip.pointerOrientation = "vertical";

    chart.cursor = new am4charts.XYCursor()
    chart.cursor.snapToSeries = series
    chart.cursor.xAxis = dateAxis

    chart.scrollbarX = new am4core.Scrollbar()
  }

  createHumidityChart() {
    const chart = am4core.create(this.humidityChartContainer, am4charts.XYChart)
    chart.data = this.humidityFormatData

    // Create axes
    const dateAxis = chart.xAxes.push(new am4charts.DateAxis())
    dateAxis.renderer.minGridDistance = 60

    chart.yAxes.push(new am4charts.ValueAxis())

    // Create series
    const series = chart.series.push(new am4charts.LineSeries())
    series.dataFields.valueY = 'value'
    series.dataFields.dateX = 'date'
    series.tooltipText = '{value}'
    series.strokeWidth = 1
    // series.tensionX = 0.8
    series.bullets.push(new am4charts.CircleBullet())

    // series.tooltip.pointerOrientation = "vertical";

    chart.cursor = new am4charts.XYCursor()
    chart.cursor.snapToSeries = series
    chart.cursor.xAxis = dateAxis

    chart.scrollbarX = new am4core.Scrollbar()
  }

  get currentTrackingData(): ITrackingData {
    return this.barcode.trackingData[this.trackingDataIndex]
  }

  get sensorData(): ISensor[] {
    return this.currentTrackingData.sensorData!
  }

  get temperatureFormatData() {
    return this.sensorData.map((sensor) => ({ date: sensor.timeOfReport, value: sensor.temperatureC }))
  }

  get humidityFormatData() {
    return this.sensorData.map((sensor) => ({ date: sensor.timeOfReport, value: sensor.humidity }))
  }

  get previousNodeName(): string | undefined {
    return this.project.trackPoints[this.currentTrackingData.currentTrackPointKey].name
  }

  get currentNode() {
    return this.project.trackPoints[this.currentTrackingData.nextTrackPointKey]
  }
}
